// ============================================================
// CursorHalo — subtle 32px halo that follows the cursor.
//
// Visible only on hover-capable devices (skips touch).
// Color shifts when hovering over interactive elements (a, button).
//
// Mount once near the root (e.g. in Layout.jsx).
//
// Usage:
//   <CursorHalo />
//
// Respects prefers-reduced-motion: silently no-op'd.
// ============================================================

import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

export default function CursorHalo() {
  const ref = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [over, setOver] = useState(false);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (reduce) return undefined;
    if (typeof window === 'undefined') return undefined;
    // Only enable on hover-capable (mouse/trackpad) devices
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    setEnabled(mq.matches);
    const onChange = () => setEnabled(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [reduce]);

  useEffect(() => {
    if (!enabled) return undefined;
    const el = ref.current;
    if (!el) return undefined;

    let rx = window.innerWidth / 2;
    let ry = window.innerHeight / 2;
    let frame = 0;

    const onMove = (e) => {
      rx = e.clientX;
      ry = e.clientY;
      // Detect interactive element under cursor
      const target = e.target;
      const isInteractive = !!target?.closest?.('a, button, [role="button"], input, textarea, select');
      setOver(isInteractive);
    };

    const tick = () => {
      const current = el.style.transform;
      const match = current?.match(/translate3d\(([\d.-]+)px,\s*([\d.-]+)px/);
      let cx = match ? parseFloat(match[1]) : rx;
      let cy = match ? parseFloat(match[2]) : ry;
      cx += (rx - cx) * 0.16;
      cy += (ry - cy) * 0.16;
      el.style.transform = `translate3d(${cx}px, ${cy}px, 0) translate(-50%, -50%)`;
      frame = requestAnimationFrame(tick);
    };

    window.addEventListener('mousemove', onMove);
    frame = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(frame);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={`pointer-events-none fixed left-0 top-0 z-[60] h-8 w-8 rounded-full mix-blend-multiply transition-[width,height,background] duration-200 ease-out ${
        over
          ? 'h-12 w-12 bg-bordeaux-600/15 ring-1 ring-bordeaux-600/40'
          : 'bg-bordeaux-600/8 ring-1 ring-bordeaux-600/25'
      }`}
      style={{ willChange: 'transform' }}
    />
  );
}
